import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import api, { formatApiErrorDetail, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const POLL_INTERVAL_MS = 4000;

export default function RoomDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [kimonoMap, setKimonoMap] = useState({});
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/kimono/catalog").then((res) => {
      const map = {};
      res.data.kimonos.forEach((k) => { map[k.id] = k; });
      setKimonoMap(map);
    }).catch(() => {});
  }, []);

  const loadRoom = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${id}`);
      setRoom(data);
      setVerses(data.verses || []);
    } catch (e) {
      toast.error("Room not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadRoom(); }, [loadRoom]);

  // polling for new verses
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/rooms/${id}/verses`);
        setVerses((prev) => (data.length !== prev.length ? data : prev));
      } catch (e) {
        // Background polling — ignore transient failures and retry next tick.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [id]);

  const nextType = verses.length % 2 === 0 ? "5-7-5" : "7-7";
  const lastAuthorId = verses.length ? verses[verses.length - 1].author_id : null;
  const isMineLast = user && lastAuthorId === user.id && verses.length > 0;
  const isHost = user && room && room.created_by === user.id;

  const addVerse = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${id}/verses`, { text });
      setVerses((p) => [...p, data]);
      setText("");
      toast.success("Verse linked");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const seal = async () => {
    try {
      await api.post(`/rooms/${id}/complete`);
      toast.success("The renga is sealed");
      loadRoom();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">Opening the room…</div>;
  if (!room) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <p className="font-serif text-2xl">This room could not be found.</p>
      <Link to="/rooms" className="text-primary mt-4 inline-block border-b border-primary/40">Back to rooms</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16" data-testid="room-detail-page">
      <Link to="/rooms" data-testid="back-to-rooms"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10">
        <ArrowLeft className="w-4 h-4" /> All rooms
      </Link>

      {/* header */}
      <div className="border-b border-border pb-10 mb-12">
        {room.banner_url && (
          <img src={mediaUrl(room.banner_url)} alt={room.title}
            className="w-full h-52 md:h-64 object-cover border border-border mb-8" data-testid="room-banner" />
        )}
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs tracking-widest uppercase text-secondary">{room.season}</span>
          {room.is_complete && (
            <span className="inline-flex items-center gap-1.5 text-xs text-primary">
              <Lock className="w-3 h-3" /> Sealed
            </span>
          )}
        </div>
        <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight" data-testid="room-title">{room.title}</h1>
        {room.theme && <p className="mt-4 text-muted-foreground italic font-serif text-lg">{room.theme}</p>}
        <p className="mt-4 text-sm text-muted-foreground">Hosted by {room.created_by_name}</p>
      </div>

      {/* verse chain */}
      <div className="space-y-10" data-testid="verse-chain">
        <AnimatePresence initial={false}>
          {verses.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-12 gap-4"
              data-testid={`verse-${i}`}
            >
              <div className="col-span-2 text-right">
                <span className="hanko text-primary/70 text-sm">{i + 1}</span>
              </div>
              <div className="col-span-10 border-l border-border pl-6">
                <p className={`font-poem whitespace-pre-line leading-relaxed ${
                  v.verse_type === "5-7-5" ? "text-2xl" : "text-2xl italic text-foreground/80"
                }`}>
                  {v.text}
                </p>
                <p className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
                  {kimonoMap[v.author_kimono || "chonin"] && (
                    <img
                      src={kimonoMap[v.author_kimono || "chonin"].image}
                      alt={kimonoMap[v.author_kimono || "chonin"].name}
                      title={`${v.author_name} · ${kimonoMap[v.author_kimono || "chonin"].name}`}
                      data-testid={`verse-robe-${i}`}
                      className="w-5 h-5 object-cover border border-border shrink-0"
                    />
                  )}
                  <span className="text-secondary">{v.verse_type}</span> · {v.author_name}
                </p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>

      {/* add verse */}
      <div className="mt-16 border-t border-border pt-10">
        {room.is_complete ? (
          <p className="text-center text-muted-foreground font-serif text-xl italic">
            This renga has been sealed. Read it as it rests.
          </p>
        ) : !user ? (
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Enter the house to link your verse.</p>
            <Link to="/login" data-testid="login-to-link"
              className="bg-primary text-primary-foreground px-6 py-3 text-sm inline-block hover:-translate-y-0.5 transition-transform">Enter</Link>
          </div>
        ) : isMineLast ? (
          <p className="text-center text-muted-foreground italic font-serif text-lg">
            You linked the last verse — wait for another poet to answer.
          </p>
        ) : (
          <form onSubmit={addVerse} data-testid="add-verse-form">
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Link the next verse · <span className="text-secondary">{nextType}</span>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={nextType === "5-7-5" ? 3 : 2}
              required
              data-testid="verse-input"
              placeholder={nextType === "5-7-5"
                ? "three lines\nfive, seven, five\nsyllables of season"
                : "two lines\nseven and seven"}
              className="w-full bg-transparent border border-border px-4 py-3 font-poem text-lg focus:border-primary outline-none transition-colors resize-none"
            />
            <div className="flex items-center justify-between mt-4">
              <button type="submit" disabled={busy} data-testid="verse-submit"
                className="bg-primary text-primary-foreground px-8 py-3 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50">
                {busy ? "Linking…" : "Link this verse"}
              </button>
              {isHost && verses.length > 1 && (
                <button type="button" onClick={seal} data-testid="seal-room"
                  className="text-sm border border-border px-5 py-3 hover:border-primary hover:text-primary transition-colors">
                  Seal the renga
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
